import type { IndicatorDirection } from './kpiDelta';
import type { TableColumn } from './tableColumns';

/**
 * Lectura VALIDADA de `config_schema`.
 *
 * <b>El problema que resuelve.</b> `config_schema` llega del catálogo como dato sin forma, y los
 * widgets lo leían con un doble cast — `cfg as unknown as { direction?: …; format?: … }` — que
 * elude el sistema de tipos por completo. Con eso, un `direction: "mayor"`, un `columns` que no
 * es un array o un `criticalVolumeThreshold: "5000"` no producen un error localizable: producen
 * comportamiento indefinido en silencio, a veces varias capas más abajo.
 *
 * Aquí la tolerancia es <b>explícita y está en un solo sitio</b>: lo que no encaja se descarta y
 * se cae al valor por defecto, que es exactamente la degradación que el módulo ya promete para
 * los widgets sembrados que no declaran nada.
 */

/** El `config_schema` de un widget, sin suponer nada de su contenido. */
export type WidgetConfig = Record<string, unknown> | undefined;

export function asConfig(config: unknown): WidgetConfig {
  return config && typeof config === 'object' && !Array.isArray(config)
    ? (config as Record<string, unknown>)
    : undefined;
}

/** Formatos de cifra que el tablero sabe pintar. */
const FORMATS = ['number', 'currency', 'percent', 'integer'] as const;
export type ValueFormat = (typeof FORMATS)[number];

/** El `format` declarado, o `undefined` si no lo declara o declara algo que no existe. */
export function readFormat(config: unknown): ValueFormat | undefined {
  const value = asConfig(config)?.format;
  return typeof value === 'string' && (FORMATS as readonly string[]).includes(value)
    ? (value as ValueFormat)
    : undefined;
}

const DIRECTIONS = ['higher-is-better', 'lower-is-better', 'neutral'] as const;

/**
 * La dirección deseable del indicador.
 *
 * `higher-is-better` por defecto, que es lo que hace que nada de lo ya sembrado cambie de
 * comportamiento al introducir esta propiedad.
 */
export function readDirection(config: unknown): IndicatorDirection {
  const value = asConfig(config)?.direction;
  return typeof value === 'string' && (DIRECTIONS as readonly string[]).includes(value)
    ? (value as IndicatorDirection)
    : 'higher-is-better';
}

/** Las columnas declaradas de una tabla, solo si son un array con la forma mínima esperada. */
export function readColumns(config: unknown): TableColumn[] | undefined {
  const value = asConfig(config)?.columns;
  if (!Array.isArray(value)) return undefined;
  const validas = value.filter((c): c is TableColumn =>
    Boolean(c) && typeof c === 'object'
    && typeof (c as TableColumn).key === 'string'
    && typeof (c as TableColumn).type === 'string');
  return validas.length > 0 ? validas : undefined;
}

/** Un número positivo declarado en `config_schema`, o el respaldo. */
export function readPositiveNumber(config: unknown, key: string, fallback: number): number {
  const value = asConfig(config)?.[key];
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

/** Una cadena no vacía declarada en `config_schema`. */
export function readString(config: unknown, key: string): string | undefined {
  const value = asConfig(config)?.[key];
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/** Un sub-objeto de `config_schema` (`series`, `image`), solo si de verdad es un objeto. */
export function readObject(config: unknown, key: string): Record<string, unknown> | undefined {
  return asConfig(asConfig(config)?.[key]);
}
